import {
  Alert,
  Button,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, VStack, Text} from 'platform/foundation';
import styled from 'styled-components';
import {z} from 'zod';

import {useEffect} from 'react';

import {
  useCreateCalendarIntegrationMutation,
  useDeleteMicrosoftCalendarOAuth2Mutation,
  useGetCalendarIntegrationQuery,
  useGetMyOutlookCalendarsQuery,
  useRevokeCalendarIntegrationMutation,
  useUpdateCalendarIntegrationMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {MyProfileFooter} from '../../../components/MyProfileFooter';

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({theme}) => theme.colors.palettes.neutral[40][100]};
`;

const calendarSyncSettingsSchema = z.object({
  isBidirectional: z.boolean(),
  syncPastEvents: z.boolean(),
  selectedCalendars: z
    .array(z.string())
    .min(1, i18n.t('page.myProfile.validation.selectAtLeastOneCalendar')),
  daysAhead: z.number().positive(i18n.t('page.myProfile.validation.daysAheadMustBePositive')),
});

type FormValues = z.infer<typeof calendarSyncSettingsSchema>;

export function CalendarSyncSettings() {
  const {
    data: calendarIntegrationData,
    error: calendarIntegrationQueryError,
    isLoading: isLoadingIntegration,
  } = useGetCalendarIntegrationQuery();
  const {data: outlookCalendarsData, isLoading: isLoadingCalendars} =
    useGetMyOutlookCalendarsQuery();
  const [createCalendarIntegration, {isLoading: isCreatingIntegration}] =
    useCreateCalendarIntegrationMutation();
  const [updateCalendarIntegration, {isLoading: isUpdatingIntegration}] =
    useUpdateCalendarIntegrationMutation();
  const [revokeCalendarIntegration, {isLoading: isRevokingIntegration}] =
    useRevokeCalendarIntegrationMutation();
  const [deleteMicrosoftCalendarOAuth2, {isLoading: isDeletingOAuth2}] =
    useDeleteMicrosoftCalendarOAuth2Mutation();

  const isLoading = isLoadingIntegration || isLoadingCalendars;
  const isSubmitting = isCreatingIntegration || isUpdatingIntegration;
  const errorStatus =
    calendarIntegrationQueryError && 'status' in calendarIntegrationQueryError
      ? calendarIntegrationQueryError.status
      : undefined;
  const hasActiveIntegration = Boolean(calendarIntegrationData) && errorStatus !== 404;

  useEffect(() => {
    if (calendarIntegrationData?.status === 'failed') {
      showNotification.error(i18n.t('page.myProfile.notifications.calendarSyncFailed'));
    }
  }, [calendarIntegrationData?.status]);

  const defaultValues: FormValues = {
    isBidirectional: calendarIntegrationData?.bidirectionalSync ?? false,
    syncPastEvents: calendarIntegrationData?.syncPastEvents ?? false,
    selectedCalendars: calendarIntegrationData?.selectedCalendarIds ?? [],
    daysAhead: calendarIntegrationData?.lookAheadDays ?? 30,
  };

  const calendarOptions =
    outlookCalendarsData?.map((calendar) => ({
      label: calendar.name,
      value: calendar.id,
    })) ?? [];

  const handleSubmit: FormSubmitHandler<FormValues> = (values) => {
    const requestBody = {
      bidirectionalSync: values.isBidirectional,
      syncPastEvents: values.syncPastEvents,
      selectedCalendarIds: values.selectedCalendars,
      lookAheadDays: values.daysAhead,
    };

    if (calendarIntegrationData) {
      return updateCalendarIntegration({
        integrationId: calendarIntegrationData.id,
        updateCalendarIntegrationRequestBody: {
          ...requestBody,
          eventTypes: calendarIntegrationData?.eventTypes ?? [],
        },
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('page.myProfile.notifications.calendarIntegrationUpdated')
          );
        })
        .catch(handleApiError);
    }

    return createCalendarIntegration({
      createCalendarIntegrationRequestBody: {
        ...requestBody,
        eventTypes: null,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.myProfile.notifications.calendarIntegrationCreated'));
      })
      .catch(handleApiError);
  };

  const handleRevoke = () => {
    if (!calendarIntegrationData) {
      return;
    }

    revokeCalendarIntegration({
      integrationId: calendarIntegrationData.id,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.myProfile.notifications.calendarIntegrationRevoked'));
      })
      .catch(handleApiError);
  };

  const handleDisconnectAccount = () => {
    openConfirmDialog({
      text: i18n.t('page.myProfile.labels.disconnectAccountConfirmation'),
      onConfirm: () =>
        deleteMicrosoftCalendarOAuth2()
          .unwrap()
          .then(() => {
            showNotification.success(
              i18n.t('page.myProfile.notifications.microsoftAccountDisconnected')
            );
          })
          .catch(handleApiError),
    });
  };

  return (
    <DataStatus isLoading={isLoading}>
      <VStack spacing={6}>
        <Form<FormValues>
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          experimentalZodSchema={calendarSyncSettingsSchema}
        >
          {(control, formApi) => (
            <Box>
              <VStack spacing={6}>
                <Alert
                  variant={hasActiveIntegration ? 'success' : 'warning'}
                  message={
                    hasActiveIntegration
                      ? i18n.t('page.myProfile.messages.calendarSyncTurnedOn')
                      : i18n.t('page.myProfile.messages.confirmInitialSyncSettings')
                  }
                />
                <FormField
                  type="switch"
                  name="isBidirectional"
                  label={i18n.t('page.myProfile.labels.bidirectionalSync')}
                  helperText={i18n.t('page.myProfile.helperTexts.bidirectionalSyncDescription')}
                  control={control}
                />
                <FormField
                  type="switch"
                  name="syncPastEvents"
                  label={i18n.t('page.myProfile.labels.syncPastEvents')}
                  helperText={i18n.t('page.myProfile.helperTexts.syncPastEventsDescription')}
                  control={control}
                />
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      type="multiChoice"
                      name="selectedCalendars"
                      label={i18n.t('page.myProfile.labels.selectedCalendars')}
                      control={control}
                      options={calendarOptions}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      type="number"
                      name="daysAhead"
                      label={i18n.t('page.myProfile.labels.daysAheadToSync')}
                      control={control}
                    />
                  </Box>
                </HStack>
                <VStack spacing={6}>
                  <Divider />
                  <Text>{i18n.t('page.myProfile.labels.disconnectCalendar')}</Text>
                  <HStack spacing={4}>
                    <Button
                      variant="secondary"
                      onClick={handleDisconnectAccount}
                      isDisabled={isSubmitting || isRevokingIntegration}
                      isLoading={isDeletingOAuth2}
                      title={i18n.t('page.myProfile.actions.disconnectAccount')}
                    />
                    {hasActiveIntegration && (
                      <Button
                        variant="secondary"
                        onClick={handleRevoke}
                        isDisabled={isSubmitting || isDeletingOAuth2}
                        isLoading={isRevokingIntegration}
                        title={i18n.t('page.myProfile.actions.cancelCalendarSync')}
                      />
                    )}
                  </HStack>
                </VStack>
                <MyProfileFooter
                  actions={[
                    {
                      type: 'button',
                      onClick: () => formApi.reset(defaultValues),
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                      isDisabled: isRevokingIntegration || isDeletingOAuth2,
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.confirm'),
                      isLoading: isSubmitting,
                      isDisabled: isRevokingIntegration || isDeletingOAuth2,
                    },
                  ]}
                />
              </VStack>
            </Box>
          )}
        </Form>
      </VStack>
    </DataStatus>
  );
}
